import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { usePeopleByIds } from '@/hooks/useCustomMentorships';

export interface MentorOption {
  id: string;
  name: string;
  /** Rol en la mentoría, si lo tiene: se muestra junto al nombre. */
  role?: 'lider' | 'apoyo';
  /** Es el mentor de apoyo del grupo (quien normalmente da sus sesiones). */
  groupSupport?: boolean;
}

interface Props {
  label: string;
  /** Mentores de la mentoría (líder y apoyos), primero. */
  mentoriaMentors: MentorOption[];
  /** Resto de mentores de la plataforma, por si la sesión la dio otro. */
  otherMentors: MentorOption[];
  value: string[];
  onChange: (ids: string[]) => void;
}

/** "mentor líder", "apoyo de la mentoría", "apoyo del grupo" o "mentor líder, apoyo del grupo". */
function describeMentorOption(m: MentorOption): string {
  const parts: string[] = [];
  if (m.role === 'lider') parts.push('mentor líder');
  if (m.groupSupport) parts.push('apoyo del grupo');
  else if (m.role === 'apoyo') parts.push('apoyo de la mentoría');
  return parts.join(', ');
}

export function MentorMultiSelect({ label, mentoriaMentors, otherMentors, value, onChange }: Props) {
  // Un registro viejo puede estar atribuido a alguien que ya no es mentor: se
  // muestra igual, marcado, para que editar el registro no lo borre sin querer.
  const known = new Set([...mentoriaMentors, ...otherMentors].map((m) => m.id));
  const missingIds = value.filter((id) => !known.has(id));
  const { data: missingPeople } = usePeopleByIds(missingIds);
  const formerMentors: MentorOption[] = missingIds.map((id) => {
    const person = missingPeople?.find((p) => p.id === id);
    return { id, name: person?.full_name || person?.email || 'Usuario' };
  });

  const toggle = (id: string, checked: boolean) =>
    onChange(checked ? [...value, id] : value.filter((x) => x !== id));

  const renderOption = (m: MentorOption) => {
    const description = describeMentorOption(m);
    return (
      <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={value.includes(m.id)} onCheckedChange={(c) => toggle(m.id, !!c)} />
        {m.name}
        {description && <span className="text-xs text-muted-foreground">({description})</span>}
      </label>
    );
  };

  if (mentoriaMentors.length === 0 && otherMentors.length === 0 && formerMentors.length === 0) return null;
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="rounded-md border p-3 space-y-3">
        {mentoriaMentors.length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-2">{mentoriaMentors.map(renderOption)}</div>}
        {otherMentors.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">Otros mentores de la plataforma</div>
            <div className="flex flex-wrap gap-x-4 gap-y-2">{otherMentors.map(renderOption)}</div>
          </div>
        )}
        {formerMentors.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">Ya no tienen rol de mentor</div>
            <div className="flex flex-wrap gap-x-4 gap-y-2">{formerMentors.map(renderOption)}</div>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Puede quedar más de uno. No multiplica las horas que se descuentan de la bolsa: es solo para el reporte por mentor.
      </p>
    </div>
  );
}
